import { useEffect, useState, useCallback } from "react";
import { http, formatApiError, formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, ArrowUp, ArrowDown, Bank as BankIcon } from "@phosphor-icons/react";

const TYPES = ["Savings", "Current", "Credit Card", "Wallet", "Cash", "Other"];
const CATS = ["Salary", "Investment", "Groceries", "Rent", "EMI", "Bills", "Dining", "Travel", "Shopping", "Health", "Other"];
const COLORS = ["#10b981", "#f59e0b", "#3b82f6", "#a855f7", "#ec4899", "#14b8a6"];

export default function Banks() {
  const [banks, setBanks] = useState([]);
  const [txns, setTxns] = useState([]);
  const [open, setOpen] = useState(false);
  const [tOpen, setTOpen] = useState(false);
  const [form, setForm] = useState({ name: "", account_type: "Savings", balance: "", account_number_last4: "", color: "#10b981" });
  const [tForm, setTForm] = useState({ bank_id: "", amount: "", txn_type: "expense", category: "Other", description: "" });

  const load = useCallback(async () => {
    const [{ data: b }, { data: t }] = await Promise.all([http.get("/banks"), http.get("/transactions", { params: { limit: 50 } })]);
    setBanks(b); setTxns(t);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addBank = async () => {
    if (!form.name || form.balance === "") { toast.error("Name & balance required"); return; }
    try {
      await http.post("/banks", { ...form, balance: parseFloat(form.balance) });
      toast.success("Bank added"); setOpen(false);
      setForm({ name: "", account_type: "Savings", balance: "", account_number_last4: "", color: "#10b981" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delBank = async (id) => {
    if (!window.confirm("Delete this account & its transactions?")) return;
    await http.delete(`/banks/${id}`); toast.success("Deleted"); load();
  };

  const addTxn = async () => {
    if (!tForm.bank_id || !tForm.amount) { toast.error("Bank & amount required"); return; }
    try {
      await http.post("/transactions", { ...tForm, amount: parseFloat(tForm.amount) });
      toast.success("Transaction logged"); setTOpen(false);
      setTForm({ bank_id: "", amount: "", txn_type: "expense", category: "Other", description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delTxn = async (id) => {
    await http.delete(`/transactions/${id}`); toast.success("Deleted"); load();
  };

  const total = banks.filter((b) => b.account_type !== "Credit Card").reduce((s, b) => s + b.balance, 0);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-bold">Accounts</div>
          <h1 className="mt-2 font-display text-4xl font-light">Banks & Cash</h1>
          <p className="text-zinc-400 mt-2 text-sm">Total cash across accounts: <span className="text-emerald-400 font-mono">{formatINR(total)}</span></p>
        </div>
        <div className="flex gap-2">
          <Dialog open={tOpen} onOpenChange={setTOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-zinc-900 border-zinc-800" data-testid="add-txn-button">
                <Plus size={16} className="mr-2" /> Log transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <DialogHeader><DialogTitle>Log a transaction</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Bank account</Label>
                  <Select value={tForm.bank_id} onValueChange={(v) => setTForm({ ...tForm, bank_id: v })}>
                    <SelectTrigger data-testid="txn-bank-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={tForm.txn_type} onValueChange={(v) => setTForm({ ...tForm, txn_type: v })}>
                    <SelectTrigger data-testid="txn-type-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount (₹)</Label>
                  <Input data-testid="txn-amount-input" type="number" step="any" value={tForm.amount}
                    onChange={(e) => setTForm({ ...tForm, amount: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={tForm.category} onValueChange={(v) => setTForm({ ...tForm, category: v })}>
                    <SelectTrigger data-testid="txn-category-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Input data-testid="txn-desc-input" value={tForm.description}
                    onChange={(e) => setTForm({ ...tForm, description: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-800" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setTOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={addTxn} data-testid="txn-save-button">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-bank-button">
                <Plus size={16} className="mr-2" /> Add bank
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <DialogHeader><DialogTitle>Add bank account</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Name</Label>
                  <Input data-testid="bank-name-input" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="HDFC Bank – Salary" className="mt-1 bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                    <SelectTrigger data-testid="bank-type-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Current balance (₹)</Label>
                  <Input data-testid="bank-balance-input" type="number" step="any" value={form.balance}
                    onChange={(e) => setForm({ ...form, balance: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label>Last 4 digits</Label>
                  <Input data-testid="bank-last4-input" value={form.account_number_last4} maxLength={4}
                    onChange={(e) => setForm({ ...form, account_number_last4: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label>Colour tag</Label>
                  <div className="flex gap-2 mt-1">
                    {COLORS.map((c) => (
                      <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                        className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${form.color === c ? "ring-2 ring-offset-2 ring-offset-zinc-950 ring-white" : ""}`}
                        style={{ backgroundColor: c }} data-testid={`bank-color-${c}`} />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={addBank} data-testid="bank-save-button">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-10">
        {banks.length === 0 && (
          <div className="col-span-full bg-zinc-900 border border-dashed border-zinc-800 rounded-lg p-12 text-center text-zinc-500">
            No accounts yet. Add your first bank →
          </div>
        )}
        {banks.map((b) => (
          <div key={b.id} className="card-hover bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid={`bank-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${b.color}22`, color: b.color }}>
                  <BankIcon size={20} weight="regular" />
                </div>
                <div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-zinc-500">{b.account_type}{b.account_number_last4 ? ` · ••${b.account_number_last4}` : ""}</div>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => delBank(b.id)} data-testid={`delete-bank-${b.id}`}>
                <Trash size={16} />
              </Button>
            </div>
            <div className="mt-6 font-display text-3xl">{formatINR(b.balance)}</div>
            <div className="mt-1 text-xs text-zinc-500">Current balance</div>
          </div>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Recent transactions</div>
          <span className="text-xs text-zinc-500">{txns.length} entries</span>
        </div>
        {txns.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No transactions logged yet.</div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {txns.map((t) => {
              const bank = banks.find((b) => b.id === t.bank_id);
              const isIncome = t.txn_type === "income";
              return (
                <div key={t.id} className="px-6 py-3 flex items-center justify-between" data-testid={`txn-row-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${isIncome ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                      {isIncome ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                    </div>
                    <div>
                      <div className="text-sm">{t.description || t.category}</div>
                      <div className="text-xs text-zinc-500">{bank?.name || "—"} · {t.category} · {new Date(t.date).toLocaleDateString("en-IN")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`font-mono text-sm ${isIncome ? "text-emerald-400" : "text-red-400"}`}>
                      {isIncome ? "+" : "-"}{formatINR(t.amount, { decimals: 0 })}
                    </div>
                    <Button size="icon" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => delTxn(t.id)}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
